import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef } from 'react';
import Heading from 'components/atoms/heading/Heading';
import { useTransitionController } from 'util/react-transition-component';
import styles from './ServicesCtaItem.module.scss';
import {
  setupMouseEnterTimeline,
  setupMouseLeaveTimeline,
  setupTransitionInTimeline,
} from './ServicesCtaItem.transitions';
import type { ServicesCtaItemProps, ServicesCtaItemRef } from './ServicesCtaItem.data';
import { renderCta } from '../../../../util/cta/cta.template';
import type { CtaRefType } from '../../../../util/cta/cta.data';
import Text from '../../../atoms/text/Text';
import useScrollTrigger from '../../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';

export default function ServicesCtaItem({
  ref,
  ...props
}: ServicesCtaItemProps & { ref?: Ref<ServicesCtaItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLLIElement | null>;
  const anchorRef = useRef<CtaRefType | HTMLDivElement>(null);
  const hoverTextRef = useRef<HTMLSpanElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HeadingRef>(null);
  const numberRef = useRef<HeadingRef>(null);

  useScrollTrigger({
    trigger: elementRef,
    animation: useSetupTimeline(setupTransitionInTimeline, {
      lineRef,
      titleRef,
      numberRef,
    }),
    vars: {
      start: 'bottom bottom',
    },
  });

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        hoverTextRef,
        backgroundRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  const onMouseEnter = useCallback(() => {
    hoverTransitionController.transitionIn();
  }, [hoverTransitionController]);

  const onMouseLeave = useCallback(() => {
    hoverTransitionController.transitionOut();
  }, [hoverTransitionController]);

  const innerTemplate = (
    <>
      <span ref={lineRef} className={styles.line} />
      <div ref={backgroundRef} className={classNames(styles.background, 'abs-fill')} />
      <Heading
        ref={numberRef}
        disableTriggerInOnScroll
        as="span"
        size="heading-04"
        className={classNames(styles.number)}
      >
        {`0${props.index + 1}`.slice(-2)}
      </Heading>
      <Heading
        ref={titleRef}
        disableTriggerInOnScroll
        as="h4"
        size="heading-04"
        transitionInType="splitRotateX3dIn"
        className={classNames(styles.title)}
      >
        {props.blok.title}
      </Heading>

      <Text
        ref={hoverTextRef}
        as="span"
        size="kanji-large"
        className={classNames(styles.hoverText)}
        transitionInType="slideUpFadeIn"
      >
        {props.blok.hover_title}
      </Text>
    </>
  );

  return (
    <li
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={elementRef}
      className={classNames(styles.servicesCtaItem, props.className)}
      {...sbEditable(props.blok)}
    >
      {props?.blok?.link?.id &&
        renderCta({
          innerTemplate,
          props: {
            label: props.blok.title,
            link: {
              href: props.blok.link.cached_url,
              uuid: props.blok.link.id,
            },
            className: classNames(styles.anchor),
          },
          elementRef: anchorRef as RefObject<CtaRefType | null>,
        })}
      {!props?.blok?.link?.id && (
        <div
          ref={anchorRef as RefObject<HTMLDivElement | null>}
          className={classNames(styles.anchor)}
        >
          {innerTemplate}
        </div>
      )}
    </li>
  );
}
